import { SystemKind } from '../../components/system-badge/system-badge.component';
import { ResultStatus, TestRunSource } from '../../models/test-run.model';

/** Ladezustand der Release-Liste der Seite; „kein Release angelegt" gilt nur bei `loaded`. */
export type ReleaseListState = 'loading' | 'error' | 'loaded';

/** Display metadata for a source system. `deferred` systems are shown but not selectable. */
export interface SystemMeta {
  key: SystemKind;
  labelKey: string;
  glyph: string;
  deferred: boolean;
}

/**
 * Quell-Systeme wie im Design. testOFFICE/Manuell/Robot/JUnit/CI-Pipeline/Runner sind aktiv — sie
 * stehen vorn; die einzelnen CI-Anbieter sind deferred (ausgegraut, "folgt spaeter").
 * `pipeline` kennt das Bundle nicht: ein Lauf mit Build-Bezug weiss nicht, von welchem
 * Anbieter er kommt, daher ein neutraler Eintrag statt einer Anbieterzuordnung (NEX-1310).
 * Auch `runner` kennt das Bundle nicht: Laeufe, die ein Runner „lokal automatisiert“ ausfuehrt
 * (Quelle `TCDL`), fielen sonst unter „Manuell“ (NEX-1515).
 */
export const SOURCE_SYSTEMS: SystemMeta[] = [
  { key: 'testoffice', labelKey: 'testResults.system.testoffice', glyph: 'tO', deferred: false },
  { key: 'manuell', labelKey: 'testResults.system.manuell', glyph: 'M', deferred: false },
  { key: 'robot', labelKey: 'testResults.system.robot', glyph: 'R', deferred: false },
  { key: 'junit', labelKey: 'testResults.system.junit', glyph: 'JU', deferred: false },
  { key: 'pipeline', labelKey: 'testResults.system.pipeline', glyph: 'CI', deferred: false },
  { key: 'runner', labelKey: 'testResults.system.runner', glyph: 'LA', deferred: false },
  { key: 'github', labelKey: 'testResults.system.github', glyph: 'GH', deferred: true },
  { key: 'gitlab', labelKey: 'testResults.system.gitlab', glyph: 'GL', deferred: true },
  { key: 'jenkins', labelKey: 'testResults.system.jenkins', glyph: 'J', deferred: true },
  { key: 'azure', labelKey: 'testResults.system.azure', glyph: 'AZ', deferred: true },
  { key: 'redmine', labelKey: 'testResults.system.redmine', glyph: 'RM', deferred: true },
];

const SYSTEM_BY_KEY = new Map<string, SystemMeta>(SOURCE_SYSTEMS.map((s) => [s.key, s]));

/** Maps the persisted run source onto a display system; unknown legacy values fall back to manuell. */
export function sourceToSystemKey(source: TestRunSource): SystemKind {
  switch (source) {
    case 'TESTOFFICE':
      return 'testoffice';
    case 'ROBOT':
      return 'robot';
    case 'JUNIT':
      return 'junit';
    case 'PIPELINE':
      return 'pipeline';
    case 'TCDL':
      return 'runner';
    default:
      return 'manuell';
  }
}

/**
 * Umkehrung von {@link sourceToSystemKey} — MUSS zusammen mit ihr gepflegt werden.
 * Jede Quelle mit eigenem Eintrag in {@link SOURCE_SYSTEMS} bildet verlustfrei hin und
 * zurueck ab.
 *
 * Die uebrigen Werte faltet `sourceToSystemKey` auf `manuell`; zurueck waehlen wir den
 * kanonischen Repraesentanten `MANUAL`. Der Filter „Manuell“ zeigt damit nur
 * {@code MANUAL}-Zeilen. Gefaltet wird nur noch {@code IMPORT}, und das ist folgenlos,
 * solange das Backend es nirgends setzt. Bekommt es einen Setzer, braucht es einen eigenen
 * Eintrag in {@link SOURCE_SYSTEMS}, so wie {@code JUNIT}, {@code PIPELINE} (NEX-1310) und
 * {@code TCDL} (NEX-1515).
 */
export function systemKeyToSource(systemKey: string): TestRunSource | null {
  switch (systemKey) {
    case 'testoffice':
      return 'TESTOFFICE';
    case 'robot':
      return 'ROBOT';
    case 'junit':
      return 'JUNIT';
    case 'pipeline':
      return 'PIPELINE';
    case 'runner':
      return 'TCDL';
    case 'manuell':
      return 'MANUAL';
    default:
      return null;
  }
}

export function systemMeta(key: string): SystemMeta {
  return SYSTEM_BY_KEY.get(key) ?? SOURCE_SYSTEMS[1];
}

/** Lowercased status used both as CSS-class suffix and i18n-key suffix. */
export function statusKey(status: ResultStatus): string {
  return status.toLowerCase();
}

const MATRIX_ICON: Record<ResultStatus, string> = {
  PASSED: 'check',
  FAILED: 'close',
  BLOCKED: 'shield',
  RUNNING: 'sync',
  SKIPPED: 'remove',
  NOT_RUN: 'radio_button_unchecked',
};

export function matrixIcon(status: ResultStatus): string {
  return MATRIX_ICON[status];
}

/** Known environments carry a fixed accent; anything else uses the neutral variant. */
export function environmentClass(environment: string | null): string {
  switch ((environment ?? '').toLowerCase()) {
    case 'staging':
      return 'staging';
    case 'test':
      return 'test';
    case 'production':
      return 'production';
    default:
      return 'other';
  }
}

/** Sichten des Reiters, Reihenfolge wie im Umschalter (page-testresultate.jsx:144-148). */
export const RESULT_VIEWS = [
  { id: 'plaene', icon: 'shield' },
  { id: 'laeufe', icon: 'bar_chart' },
  { id: 'testfaelle', icon: 'check' },
] as const;

export type ResultView = (typeof RESULT_VIEWS)[number]['id'];

const DEFAULT_RESULT_VIEW: ResultView = 'plaene';

/** Liest den Query-Parameter `sicht`; fehlende oder unbekannte Werte landen auf „Testpläne". */
export function parseResultView(value: string | null): ResultView {
  return RESULT_VIEWS.find((v) => v.id === value)?.id ?? DEFAULT_RESULT_VIEW;
}
